import {StaticConfig} from 'config/staticConfig';

const authenticationRoutes = [
  '/login',
  '/login/error',
  '/login/system-error',
  '/login/unsupported-browser',
  '/implicit/callback',
  '/messenger/callback',
  '/forced-logout',
  '/mobile-app-info'
] as const;

const createAccountRoutes = [
  '/create-account',
  '/create-account/expired',
  '/create-account/loginID',
  '/create-account/accountVerification',
  '/create-account/confirmation',
  '/create-account/authorization/:token',
  '/create-account/success'
] as const;

const billingRoutes = [
  '/billing',
  '/billing/overview',
  '/billing/autopay',
  '/billing/payment-methods',
  '/billing/makeapayment',
  '/billing/makeapayment/:step',
  '/billing/makeapayment/paypolicies',
  '/billing/makeapayment/paymentmodal',
  '/billing/makeapayment/quickpay/paymentmodal',
  '/billing/makeapayment/:policyNumber/:step',
  '/billing/makeapayment/:policyNumber/singlepay',
  '/billing/statements',
  '/billing/transactions',
  '/billing/change-payment-plan/:policyNumber',
  '/billing/change-payment-plan/:policyNumber/select-payment-plan',
  '/billing/change-payment-plan/:policyNumber/review-payment-plan',
  '/billing/change-payment-plan/:policyNumber/confirmation-payment-plan'
] as const;

const claimRoutes = [
  '/claims',
  '/claims/:fileNumber',
  '/claims/:fileNumber/overview',
  '/claims/:fileNumber/coverages',
  '/claims/:fileNumber/documents',
  '/claims/:fileNumber/messenger',
  '/claims/:fileNumber/settings',
  '/claims/fnol',
  '/claims/fnol/intro',
  '/claims/fnol/loss-date',
  '/claims/fnol/select-policy',
  '/claims/fnol/other-vehicle',
  '/claims/fnol/loss-description',
  '/claims/fnol/auto-glass',
  '/claims/fnol/auto-glass-damage-locations',
  '/claims/fnol/auto-glass-incident-type',
  '/claims/fnol/tow-amount',
  '/claims/fnol/was-vehicle-recovered',
  '/claims/fnol/was-vehicle-damaged',
  '/claims/fnol/auto-damage-description',
  '/claims/fnol/home-loss-description',
  '/claims/fnol/was-weather-related',
  '/claims/fnol/home-loss-weather-related',
  '/claims/fnol/home-loss-not-weather-related',
  '/claims/fnol/vehicle-location',
  '/claims/fnol/auto-driver',
  '/claims/fnol/was-anyone-injured',
  '/claims/fnol/loss-location',
  '/claims/fnol/additional-information',
  '/claims/fnol/contact-information',
  '/claims/fnol/text-notification-enrollment',
  '/claims/fnol/review',
  '/claims/fnol/confirm',
  '/claims/fnol/prelogin',
  '/claims/fnol/prelogin/intro',
  '/claims/fnol/prelogin/select-policy',
  '/claims/fnol/prelogin/who-is-filing',
  '/claims/fnol/prelogin/home-incident-details',
  '/claims/fnol/prelogin/auto-glass-damage',
  '/claims/fnol/prelogin/marine-glass-damage',
  '/claims/fnol/prelogin/policy-details',
  '/claims/fnol/prelogin/auto-incident-details',
  '/claims/fnol/prelogin/marine-incident-details',
  '/claims/fnol/prelogin/was-weather-related',
  '/claims/fnol/prelogin/auto-vehicle-info',
  '/claims/fnol/prelogin/marine-vessel-info',
  '/claims/fnol/prelogin/type-of-damage',
  '/claims/fnol/prelogin/incident-date-time',
  '/claims/fnol/prelogin/additional-details',
  '/claims/fnol/prelogin/additional-details-death',
  '/claims/fnol/prelogin/additional-details-disability',
  '/claims/fnol/prelogin/incident-location',
  '/claims/fnol/prelogin/who-is-reporting',
  '/claims/fnol/prelogin/contact-info',
  '/claims/fnol/prelogin/summary',
  '/claims/fnol/prelogin/confirmation',
  '/claims/fnol/prelogin/submission-error',
  '/claims/prelogin/upload',
  '/claims/prelogin/upload/claim-info',
  '/claims/prelogin/upload/contact-info',
  '/claims/prelogin/upload/file-select',
  '/claims/prelogin/upload/file-upload',
  '/claims/prelogin/upload/system-outage'
] as const;

const policyRoutes = [
  '/policies',
  '/policies/active',
  '/policies/archived',
  '/policies/:policyNumber',
  '/policies/:policyNumber/overview',
  '/policies/:policyNumber/coverages',
  '/policies/:policyNumber/discounts',
  '/policies/:policyNumber/documents',
  '/policies/:policyNumber/id-cards',
  '/policies/:policyNumber/editmortgagee',
  '/policies/:policyNumber/editmortgagee/landing',
  '/policies/:policyNumber/editmortgagee/select-lender',
  '/policies/:policyNumber/editmortgagee/mortgage-help',
  '/policies/:policyNumber/editmortgagee/search-lender',
  '/policies/:policyNumber/editmortgagee/mortgage-escrow-info',
  '/policies/:policyNumber/editmortgagee/review',
  '/policies/:policyNumber/editmortgagee/confirmation',
  '/policies/:policyNumber/updatelender',
  '/policies/:policyNumber/updatelender/:vehicleId/review',
  '/policies/:policyNumber/updatelender/:vehicleId/confirmation',
  '/policies/:policyNumber/updatelender/select',
  '/selfservice/:policyNumber',
  '/selfservice/:policyNumber/edit-auto-coverage',
  '/selfservice/:policyNumber/edit-home-coverage',
  '/selfservice/:policyNumber/quote-driver',
  '/selfservice/:policyNumber/quote-add-vehicle',
  '/selfservice/:policyNumber/quote-add-vehicle/start',
  '/selfservice/:policyNumber/quote-add-vehicle/intro',
  '/selfservice/:policyNumber/quote-add-vehicle/specify-vehicle',
  '/selfservice/:policyNumber/quote-add-vehicle/enter-year-make-model',
  '/selfservice/:policyNumber/quote-add-vehicle/purchase-details',
  '/selfservice/:policyNumber/quote-add-vehicle/enter-vin-for-quote',
  '/selfservice/:policyNumber/quote-add-vehicle/vehicle-details',
  '/selfservice/:policyNumber/quote-add-vehicle/additional-vehicle-details',
  '/selfservice/:policyNumber/quote-add-vehicle/edit-coverages-for-quote',
  '/selfservice/:policyNumber/quote-add-vehicle/edit-coverages-for-bind',
  '/selfservice/:policyNumber/quote-add-vehicle/enter-vin-for-bind',
  '/selfservice/:policyNumber/quote-add-vehicle/lender-search',
  '/selfservice/:policyNumber/quote-add-vehicle/review',
  '/selfservice/:policyNumber/quote-add-vehicle/confirmation',
  '/selfservice/:policyNumber/remove-vehicle'
] as const;

const profileRoutes = [
  '/profile',
  '/profile/contact-information',
  '/profile/contact-information/update-life-address',
  '/profile/contact-information/manage-emails',
  '/profile/contact-information/manage-emails/add-email',
  '/profile/contact-information/manage-phones',
  '/profile/contact-information/manage-phones/add-phone',
  '/profile/paperless-preferences',
  '/profile/paperless-preferences/edit/life',
  '/profile/paperless-preferences/edit/:quadrantName/:accountNumber',
  '/profile/notifications',
  '/profile/notifications/enroll',
  '/profile/security',
  '/profile/security/password',
  '/profile/security/mfa',
  '/profile/security/loginId',
  '/profile/eproxy'
] as const;

const quotingRoutes = [
  '/quoting',
  '/quoting/:quoteTypePrefix/:section',
  '/quoting/:quoteTypePrefix/summary',
  '/quoting/:quoteTypePrefix/bindConfirmation',
  '/quoting/return',
  '/quoting/life',
  '/quoting/life/summary'
] as const;

const onboardingRoutes = [
  '/onboarding',
  '/onboarding/esign',
  '/onboarding/esign/:envelopeId',
  '/onboarding/payment',
  '/onboarding/confirmation',
  '/onboarding/welcome',
  '/onboarding/error',
  '/onboarding/notifications',
  '/onboarding/paperless-preferences/:quadrantName/:accountNumber',
  '/onboarding/autoPay',
  '/onboarding/paperless-preferences',
  '/onboarding/upload-documents'
] as const;

const globalDocumentRoutes = [
  '/documents',
  '/documents/viewDocuments',
  '/documents/viewDocuments/:categoryAll',
  '/documents/viewDocuments/:categoryAll(policies|billing|claims|forms|esign)',
  '/documents/viewDocuments/policy/:policyNumber',
  '/documents/viewDocuments/claim/:fileNumber',
  '/documents/viewDocuments/billing/:billType',
  '/documents/viewDocuments/esign',
  '/documents/viewDocuments/esign/:envelopeId',
  '/documents/upload',
  '/documents/upload/:step',
  '/documents/upload/step1',
  '/documents/upload/step2',
  '/documents/upload/step3',
  '/documents/id-cards'
] as const;

const preloginSelfServiceRoutes = [
  '/express-id',
  '/pay-as-guest',
  '/pay-as-guest/find-bill',
  '/pay-as-guest/mutual-amount',
  '/pay-as-guest/payment-information',
  `/pay-as-guest/make-payment`,
  '/pay-as-guest/life-amount'
] as const;

const homePhotoToolRoutes = [
  '/photo-upload/home',
  '/photo-upload/home/:photoId/:claimsFileNumber',
  '/photo-upload/home/upload/:photoId/:claimsFileNumber',
  '/photo-upload/home/additionalDetails/:photoId/:claimsFileNumber',
  '/photo-upload/home/finish/:photoId/:claimsFileNumber'
] as const;

const miscellaneousRoutes = ['/account'] as const;

const routes = [
  '/',
  ...authenticationRoutes,
  ...createAccountRoutes,
  ...billingRoutes,
  ...claimRoutes,
  ...policyRoutes,
  ...profileRoutes,
  ...quotingRoutes,
  ...onboardingRoutes,
  ...globalDocumentRoutes,
  ...preloginSelfServiceRoutes,
  ...homePhotoToolRoutes,
  ...miscellaneousRoutes
] as const;

const AEMLanguage = 'en';
const calculatedRoutes = {
  contactus: `${StaticConfig.baseAemApi}/${AEMLanguage}/customer-service/contact-us.html`,
  timeout: `${StaticConfig.baseAemApi}/${AEMLanguage}/errors/timeout.html`
} as const;

export const getNavRoutesForTesting = (): string[] => [...routes];

export type RouteName = (typeof routes)[number];
type CalculatedRouteName = keyof typeof calculatedRoutes;

const renderRoute = (route: string, params: {[param: string]: string} = {}): string =>
  Object.keys(params).reduce<string>(
    (renderedRoute, paramName) => renderedRoute.replace(`:${paramName}`, params[paramName]),
    route
  );

export const getRoute = (route: RouteName, params: {[param: string]: string} = {}): string =>
  renderRoute(route, params);

export const getSubRoute = (
  route: RouteName,
  params: {[param: string]: string} = {},
  subRouteSegments: number = 1
): string =>
  renderRoute(route, params)
    .split('/')
    .slice(subRouteSegments * -1)
    .join('/');

export const getRouteWithMobileTokens = (route: RouteName): string =>
  route.replace(
    /:(\w[^/]*?)(\/|$)/g,
    (_, tokenName: string, remainingString: string) => `___${tokenName.toUpperCase()}___${remainingString}`
  );

export const generateRouteMap = (): string[] => [
  ...routes.map((route) => getRouteWithMobileTokens(route)),
  '/documents/viewDocuments/billing' //Special case routing
];

export const getCalculatedRoute = (key: CalculatedRouteName, params: {[param: string]: string} = {}): string =>
  renderRoute(calculatedRoutes[key], params);
